import React, { FC, useEffect, useState } from 'react'

import { PageLayout } from 'Components'
import { ErrorMessage } from 'Pages/ErrorPage'
import { customerSelector } from 'ReduxStore/customerSlice'
import { useAppSelector } from 'ReduxStore/hooks'
import { memberShipService } from 'Services'

type Response = undefined | { url: undefined | string; status: number }

// Hardcoded plan variant IDs for Google and Apple IAP work as hotfix.
// The frontend also checks for the plan variant name to include 'google' or 'apple' as a fallback.
// TODO: Remove this once we have a more robust solution in place.

const GOOGLE_IAP_PLANVARIANT_IDS = ['69b1a6548e4649ebc9c3773a', '69b1a84eb62a29aaee5963c3', '69b1a8d0bb4776a3295af62f']

const APPLE_IAP_PLANVARIANT_IDS = ['6641f26193162e1da1b39466', '698de71634788e5c123f706f', '698de7912e55de78f17e31b4']

type CustomerPlanVariantId = string
type CustomerPlanVariantName = string

export const UserMasterDataPage: FC = () => {
  const { id: customerId } = useAppSelector(customerSelector)
  const [response, setResponse] = useState<Response>()
  const [planVariantId, setPlanVariantId] = useState<CustomerPlanVariantId>('')
  const [planVariantName, setPlanVariantName] = useState<CustomerPlanVariantName>('')

  useEffect(() => {
    let mounted = true

    memberShipService.getTrackingInfo().then((planVariant) => {
      if (mounted) {
        setPlanVariantId(planVariant?.planVariantId ?? '')
        setPlanVariantName(planVariant?.planVariantName ?? '')
      }
    })

    memberShipService.getSelfServiceUrl(customerId).then((response) => {
      if (mounted) {
        setResponse(response)
      }
    })

    return () => {
      mounted = false
    }
  }, [customerId])

  if (GOOGLE_IAP_PLANVARIANT_IDS.includes(planVariantId) || planVariantName.toLowerCase().includes('google')) {
    return (
      <PageLayout variant="smallcircle">
        <div className="container max-700">
          <h1>Abonnement in der eBalance-App</h1>
          <p>
            Sie haben eBalance direkt in der eBalance-App abonniert. In diesem Fall erfolgt die Kaufabwicklung über den
            Google Play Store.
          </p>
          <p>
            Externe Abonnements können leider nicht im eBalance-Portal verwaltet werden. Um Ihr Abonnement einzusehen
            oder zu kündigen, gehen Sie bitte zu{' '}
            <a href="https://play.google.com/store/account/subscriptions" target="_blank" rel="noreferrer">
              https://play.google.com/store/account/subscriptions
            </a>
            . Alternativ können Sie das Abonnement auch auf Ihrem Android-Gerät im Google Play Store verwalten.
          </p>
        </div>
      </PageLayout>
    )
  }

  if (APPLE_IAP_PLANVARIANT_IDS.includes(planVariantId) || planVariantName.toLowerCase().includes('apple')) {
    return (
      <PageLayout variant="smallcircle">
        <div className="container max-700">
          <h1>Abonnement in der eBalance-App</h1>
          <p>
            Sie haben eBalance direkt in der eBalance-App abonniert. In diesem Fall erfolgt die Kaufabwicklung über den
            Apple App Store.
          </p>
          <p>
            Externe Abonnements können leider nicht im eBalance-Portal verwaltet werden. Um Ihr Abonnement einzusehen
            oder zu kündigen, gehen Sie bitte zu{' '}
            <a href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noreferrer">
              https://apps.apple.com/account/subscriptions
            </a>
            . Alternativ können Sie das Abonnement auch in den Einstellungen Ihres iPhones beziehungsweise iPads
            verwalten.
          </p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <UserMasterDataContent response={response} />
    </PageLayout>
  )
}

const UserMasterDataContent: FC<{ response: Response }> = ({ response }) => {
  if (!response) {
    return null
  }

  if (response.url) {
    return <iframe src={response.url} style={{ height: '75vh', width: '100vw', border: 0 }}></iframe>
  }

  return (
    <div className="container max-700">
      {response.status === 404 ? (
        <p>
          Wir führen Umbauarbeiten durch, daher können Sie Ihre Kunden- und Mitgliedsdaten vorübergehend nicht selbst
          abrufen. Unser Support-Team gibt Ihnen in der Zwischenzeit jedoch gern Auskunft über Ihre Daten. Schreiben Sie
          uns einfach eine E-Mail: <a href="mailto:info@eBalance.ch">info@eBalance.ch</a>
        </p>
      ) : (
        <ErrorMessage />
      )}
    </div>
  )
}
